import { defineComponent as _defineComponent } from 'vue';
import { unref as _unref, toDisplayString as _toDisplayString, withModifiers as _withModifiers, withKeys as _withKeys, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, isRef as _isRef, createVNode as _createVNode, withCtx as _withCtx, createSlots as _createSlots, createTextVNode as _createTextVNode, createElementVNode as _createElementVNode, Fragment as _Fragment } from "vue";
const _hoisted_1 = ["onKeydown"];
const _hoisted_2 = ["onKeydown"];
const _hoisted_3 = ["disabled"];
import { mergeComponentClasses } from "@/common/helpers/style";
import { useContext } from "@/store/context";
import { useSegmentsStore } from "@/store/segments";
import { useVuelidate } from "@vuelidate/core";
import { helpers, required } from "@vuelidate/validators";
import { onKeyStroke, useVModel } from "@vueuse/core";
import { storeToRefs } from "pinia";
import { computed, defineAsyncComponent, inject, nextTick, onMounted, ref, watch } from "vue";
// Components
import RecSelect from "@/common/components/RecSelect.vue";
import { SegmentStatus, SegmentType } from "@/types/segments";
// Async Components
export default /*@__PURE__*/ _defineComponent({
    ...{ inheritAttrs: false },
    __name: 'SegmentsPicker',
    props: {
        modelValue: {},
        menu: { type: Boolean },
        label: { default: undefined },
        hideHeader: { type: Boolean, default: false },
        menuMaxHeight: { default: 0 },
        itemValue: { default: "segmentId" },
        inputName: { default: undefined },
        matchCriteriaInputName: {},
        matchCriteriaInputValue: { default: undefined },
        options: { default: undefined },
        disabledOptions: { default: () => [] },
        type: { default: "custom" },
        disabled: { type: Boolean },
        buttonClass: {},
        activeButtonClass: {},
        tagLimit: { default: undefined },
        hideMenuTags: { type: Boolean },
        clearable: { type: Boolean, default: true },
        segmentType: { default: undefined },
        tooltip: {},
        buttonPrependIcon: { default: undefined },
        highlightSelected: { type: Boolean, default: true },
        highlightEmpty: { type: Boolean, default: false },
        iconOnly: { type: Boolean },
        maxLabelLength: { default: 20 },
        menuPosition: {},
        placement: {},
        getDisplayValue: { type: Function, default: undefined }
    },
    emits: ["update:modelValue", "update:menu", "update:matchCriteriaInputValue", "create-segment", "clear"],
    setup(__props, { emit: __emit }) {
        const RecCardContent = defineAsyncComponent(() => import("@/common/components/RecCardContent.vue"));
        const RecCardFooter = defineAsyncComponent(() => import("@/common/components/RecCardFooter.vue"));
        const RecCardTitle = defineAsyncComponent(() => import("@/common/components/RecCardTitle.vue"));
        const RecDialog = defineAsyncComponent(() => import("@/common/components/RecDialog.vue"));
        const RecRadioButtons = defineAsyncComponent(() => import("@/common/components/RecRadioButtons.vue"));
        const RecTextField = defineAsyncComponent(() => import("@/common/components/RecTextField.vue"));
        // Setup
        const props = __props;
        const emit = __emit;
        const i18n = inject("i18n");
        // We need this ref for when new segments are added AND an `options` prop is provided. Otherwise, the page would have
        // to be reloaded.
        const optionsRef = ref();
        watch(() => props.options, (newOptions) => {
            optionsRef.value = newOptions;
        }, { immediate: true, deep: true });
        const isMultiselect = computed(() => Array.isArray(props.modelValue));
        const segmentsStore = useSegmentsStore();
        const segmentsMap = computed(() => new Map((optionsRef.value ?? segmentsStore.segments).map((s) => [s[props.itemValue], s])));
        const contextStore = useContext();
        const { isAnalyst, isModerator, currentStudyId } = storeToRefs(contextStore);
        const model = computed({
            get: () => {
                if (isMultiselect.value) {
                    return props.modelValue.filter((item) => segmentsMap.value.has(item));
                }
                if (props.modelValue === undefined || !segmentsMap.value.has(props.modelValue)) {
                    return undefined;
                }
                return props.modelValue;
            },
            set: (val) => {
                emit("update:modelValue", val);
            }
        });
        const menuModel = useVModel(props, "menu", emit, {
            defaultValue: false,
            passive: true
        }); // TODO: use defineModel instead
        const isInactive = computed(() => model.value === undefined || (isMultiselect.value && !model.value.length));
        const computedButtonClass = computed(() => isInactive.value ? props.buttonClass : mergeComponentClasses(props.buttonClass, props.activeButtonClass));
        const isSelected = (id) => {
            if (isMultiselect.value) {
                return model.value.includes(id);
            }
            return model.value === id;
        };
        const getSegmentTag = (segment) => {
            if (segment.status === SegmentStatus.Deleted) {
                throw new Error("Deleted segments should not be visible in the segment picker");
            }
            // General scope segments picker.
            // ...no tags.
            if (currentStudyId.value === undefined)
                return undefined;
            // Study scope segments picker.
            // ...a segment should have an "Archived" tag if it is archived but used in the scoped study.
            const id = segment[props.itemValue];
            const selected = isSelected(id);
            if (segment.status === SegmentStatus.Archived && selected) {
                return { tag: i18n.t("Archived"), tagClass: "desat" };
            }
            // ...a segment should have a "Hidden" tag if it is not visible to the scoped study but yet used in it.
            if (segment.status === SegmentStatus.Active &&
                !segment.studyVisibilityIds.includes(currentStudyId.value) &&
                !segment.visibleToAllStudies) {
                return { tag: i18n.t("Hidden"), tagClass: "danger" };
            }
            // ...a segments should have no tag if it is active and visible to the scoped study.
            return undefined;
        };
        const computedOptions = computed(() => {
            const filteredSegments = optionsRef.value ??
                segmentsStore.segments.filter((segment) => {
                    const selected = isSelected(segment[props.itemValue]);
                    // Filter by type + No deleted segments allowed
                    if ((props.segmentType && segment.type !== props.segmentType) ||
                        segment.status === SegmentStatus.Deleted ||
                        (!selected && segment.status === SegmentStatus.Archived))
                        return false;
                    // If not in a study scope, only active segments are allowed
                    if (currentStudyId.value === undefined)
                        return segment.status === SegmentStatus.Active;
                    // If in a study scope, only visible or selected segments are allowed
                    return segment.visibleToAllStudies || segment.studyVisibilityIds.includes(currentStudyId.value) || selected;
                });
            return [
                {
                    options: filteredSegments
                        .map((s) => {
                        const value = props.itemValue === "name" ? String(s[props.itemValue]) : s[props.itemValue];
                        const label = s.name;
                        let disabled;
                        switch (props.disabledOptions) {
                            case "all":
                                disabled = true;
                                break;
                            case "smart":
                                disabled = s.type === SegmentType.Smart;
                                break;
                            default:
                                disabled = props.disabledOptions.includes(value);
                        }
                        const { tag, tagClass } = getSegmentTag(s) ?? {};
                        const prependIcon = {
                            icon: s.type === SegmentType.Smart ? "icon-tag-refresh" : "icon-tag",
                            tooltip: s.type === SegmentType.Smart ? i18n.t("Smart Segment") : i18n.t("Basic Segment")
                        };
                        return { ...s, value, label, disabled, tag, tagClass, prependIcon };
                    })
                        .sort((a, b) => a.name.localeCompare(b.name))
                }
            ];
        });
        const navigateToManageSegments = () => {
            const { primaryDomain } = storeToRefs(useContext());
            const { protocol } = window.location;
            window.open(`${protocol}//${primaryDomain.value}/admin/v2/segments`, "_blank");
        };
        const el = ref();
        const placeholder = computed(() => {
            // Always use prop label if is string is passed
            if (typeof props.label === "string")
                return props.label;
            if (props.segmentType === SegmentType.Smart)
                return i18n.t("Smart Segments");
            if (props.segmentType === SegmentType.Basic)
                return i18n.t("Basic Segments");
            return i18n.t("Segments");
        });
        const computedLabel = computed(() => {
            // This would happen if SegmentsPickerApp.vue > props.labelVariant is set to LabelVariant.Empty
            if (props.iconOnly)
                return "";
            // Function props.getDisplayValue is always used when provided
            if (typeof props.getDisplayValue === "function") {
                return props.getDisplayValue(model.value);
            }
            // To be shown when no selection is made
            if (isInactive.value)
                return placeholder.value;
            // To be shown when some items are selected
            let txt;
            switch (props.itemValue) {
                case "name":
                    txt = isMultiselect.value ? model.value.join(", ") : model.value;
                    break;
                default:
                    txt = isMultiselect.value
                        ? // Casting here is safe since we make sure model.value is only made of existing segment(s)
                            model.value.map((id) => segmentsMap.value.get(id).name).join(", ")
                        : segmentsMap.value.get(model.value).name;
            }
            const itemsCount = model.value.length;
            let labelWidth = 0;
            let maxWidth = 0;
            if (isMultiselect.value && el.value?.labelEl.parentNode) {
                // Build a temp label span to measure its width and determine if it should be replaced
                const tmpSpan = document.createElement("SPAN");
                tmpSpan.classList.add("rec-select__label", "rec-select__label--placeholder");
                tmpSpan.style.position = "absolute";
                tmpSpan.style.whiteSpace = "nowrap";
                el.value.labelEl.parentNode.insertBefore(tmpSpan, el.value.labelEl.nextSibling);
                // evaluate max label width using a dummy string of props.maxLabelLength characters
                tmpSpan.textContent = String("@").repeat(props.maxLabelLength);
                maxWidth = tmpSpan.offsetWidth;
                // evaluate current label width using ideal label's string
                tmpSpan.textContent = txt;
                labelWidth = tmpSpan.offsetWidth;
                el.value.labelEl.parentNode.removeChild(tmpSpan);
            }
            if ((isMultiselect.value && props.maxLabelLength < txt.length) || (labelWidth && maxWidth < labelWidth)) {
                txt = i18n.t("{0} selected", [itemsCount]);
            }
            return matchCriteria.value ? `${txt} (${i18n.t("All")})` : txt;
        });
        const addSegmentDialog = ref(false);
        const onAddSegment = () => {
            addSegmentDialog.value = true;
        };
        const newSegmentForm = ref({
            name: "",
            type: SegmentType.Basic,
            studyVisibilityIds: currentStudyId.value === undefined ? [] : [currentStudyId.value],
            visibleToAllStudies: currentStudyId.value === undefined
        });
        // Add Segment form validation
        const isRequired = helpers.withMessage(i18n.t("Required"), required);
        const nameIsUnique = helpers.withMessage(i18n.t("Segment name must be unique"), (val) => ![...segmentsMap.value.values()].some((s) => val.trim() === s.name));
        const validationRules = {
            name: { isRequired, nameIsUnique }
        };
        // https://vuelidate-next.netlify.app/advanced_usage.html#scope-property
        const validations = useVuelidate(validationRules, newSegmentForm, { $scope: false, $rewardEarly: true });
        const resetSegmentForm = () => {
            newSegmentForm.value.name = "";
            addSegmentDialog.value = false;
            validations.value.$reset();
        };
        const onSaveSegmentForm = async () => {
            if (!newSegmentForm.value.name?.length)
                return;
            await validations.value.$validate();
            if (validations.value.$error)
                return;
            const { visibleToAllStudies, studyVisibilityIds, ...form } = newSegmentForm.value;
            const payload = visibleToAllStudies ? { ...form, visibleToAllStudies } : { ...form, studyVisibilityIds };
            const segment = await segmentsStore.createSegment(payload, currentStudyId.value);
            if (!segment)
                return;
            if (optionsRef.value !== undefined)
                optionsRef.value.push(segment);
            emit("create-segment", segment);
            resetSegmentForm();
            if (!isMultiselect.value)
                return;
            // For Multiselect pickers only: Select and scroll newly added segment into view
            await nextTick();
            model.value = [...model.value, segment[props.itemValue]];
            await nextTick(); // DOM is going to be updated on model.value update.
            const index = computedOptions.value[0].options.findIndex((opt) => opt.value === segment[props.itemValue]);
            if (index === -1)
                return; // Index not found - ...this shouldn't happen...
            const listEl = document.querySelectorAll(".rec-segments-picker .rec-dropdown.rec-dropdown--open .rec-select-menu__options__scroll")[0];
            if (!listEl || !listEl.children[index])
                return; // List item not found - ...this shouldn't happen...}
            // ...and scroll!!!
            listEl?.scrollTo({ behavior: "smooth", top: listEl.children[index].offsetTop });
        };
        const addButton = ref();
        const onCloseAddSegmentDialog = () => {
            resetSegmentForm();
            addSegmentDialog.value = false;
            addButton.value?.focus();
        };
        onKeyStroke("Enter", () => {
            menuModel.value = false;
        });
        const onClear = () => {
            emit("clear");
        };
        const matchCriteria = useVModel(props, "matchCriteriaInputValue", emit); // TODO: use defineModel instead
        const matchCriteriaItems = computed(() => [
            { value: 0, label: i18n.t("Match Any") },
            { value: 1, label: i18n.t("Match All") }
        ]);
        const showMatchCriteria = computed(() => {
            return props.matchCriteriaInputName || props.matchCriteriaInputValue !== undefined;
        });
        // This makes the option elements initially rendered in the DOM only if there are less than 250 options.
        // Otherwise, they won't be rendered in order to allow the SegmentsPicker component to load faster. When
        // that is the case, the menu state will be watched until the menu is opened (option elements rendered)
        // to make the menu use v-show instead of v-if.
        const useVShow = ref(computedOptions.value[0].options.length < 250);
        const stopMenuWatcher = ref();
        onMounted(() => {
            if (computedOptions.value[0].options.length < 250)
                return;
            stopMenuWatcher.value = watch(menuModel, (next) => {
                if (!next || !stopMenuWatcher.value)
                    return;
                useVShow.value = true;
                stopMenuWatcher.value();
            });
        });
        return (_ctx, _cache) => {
            return (_openBlock(), _createElementBlock(_Fragment, null, [
                _createVNode(RecSelect, {
                    ref_key: "el",
                    ref: el,
                    modelValue: model.value,
                    "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event) => ((model).value = $event)),
                    menu: _unref(menuModel),
                    "onUpdate:menu": _cache[2] || (_cache[2] = ($event) => (_isRef(menuModel) ? (menuModel).value = $event : null)),
                    class: "rec-segments-picker",
                    "input-name": _ctx.inputName,
                    options: computedOptions.value,
                    label: computedLabel.value,
                    "hide-header": _ctx.hideHeader,
                    "menu-title": _ctx.$t('Segments'),
                    "tag-limit": _ctx.tagLimit,
                    "show-selection-count-in-label": false,
                    "show-selection-menu-tag-area": isMultiselect.value && !_ctx.hideMenuTags,
                    "button-class": computedButtonClass.value,
                    "button-prepend-icon": _ctx.buttonPrependIcon,
                    "highlight-selected": _ctx.highlightSelected,
                    "highlight-empty": _ctx.highlightEmpty,
                    disabled: _ctx.disabled,
                    type: _ctx.type,
                    "ignore-click-outside": ['.p-dialog-mask:has(.rec-segments-picker__dialog)'],
                    clearable: _ctx.clearable,
                    tooltip: _ctx.tooltip,
                    "menu-position": _ctx.menuPosition,
                    "use-v-show": useVShow.value,
                    "menu-max-height": _ctx.menuMaxHeight,
                    "search-input-autofocus": "",
                    "filter-search-on-input": "",
                    placement: _ctx.placement,
                    onClear: onClear
                }, _createSlots({ _: 2 }, [
                    (_unref(isModerator))
                        ? {
                            name: "menu-controls",
                            fn: _withCtx(() => [
                                (_unref(isAnalyst))
                                    ? (_openBlock(), _createElementBlock("button", {
                                        key: 0,
                                        class: "btn btn-sm btn-link",
                                        type: "button",
                                        onClick: navigateToManageSegments,
                                        onKeydown: _withKeys(_withModifiers(navigateToManageSegments, ["stop"]), ["enter"])
                                    }, _toDisplayString(_ctx.$t("Manage")), 41, _hoisted_1))
                                    : _createCommentVNode("", true),
                                (_unref(isModerator) && _ctx.segmentType !== _unref(SegmentType).Smart)
                                    ? (_openBlock(), _createElementBlock("button", {
                                        key: 1,
                                        ref_key: "addButton",
                                        ref: addButton,
                                        class: "btn btn-sm btn-border",
                                        type: "button",
                                        onClick: _withModifiers(onAddSegment, ["stop"]),
                                        onKeydown: _withKeys(_withModifiers(onAddSegment, ["stop"]), ["enter"])
                                    }, _toDisplayString(_ctx.$t("Add")), 41, _hoisted_2))
                                    : _createCommentVNode("", true)
                            ]),
                            key: "0"
                        }
                        : undefined,
                    (showMatchCriteria.value)
                        ? {
                            name: "content-top",
                            fn: _withCtx(({ hasOptions }) => [
                                _createVNode(_unref(RecRadioButtons), {
                                    modelValue: _unref(matchCriteria),
                                    "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event) => (_isRef(matchCriteria) ? (matchCriteria).value = $event : null)),
                                    items: matchCriteriaItems.value,
                                    name: _ctx.matchCriteriaInputName,
                                    disabled: !hasOptions
                                }, null, 8, ["modelValue", "items", "name", "disabled"])
                            ]),
                            key: "1"
                        }
                        : undefined
                ]), 1032, ["modelValue", "menu", "input-name", "options", "label", "hide-header", "menu-title", "tag-limit", "show-selection-menu-tag-area", "button-class", "button-prepend-icon", "highlight-selected", "highlight-empty", "disabled", "type", "clearable", "tooltip", "menu-position", "use-v-show", "menu-max-height", "placement"]),
                _createVNode(_unref(RecDialog), {
                    modelValue: addSegmentDialog.value,
                    "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event) => ((addSegmentDialog).value = $event)),
                    class: "rec-segments-picker__dialog",
                    "close-on-click-outside": false,
                    size: "large",
                    onKeydown: [
                        _withKeys(onCloseAddSegmentDialog, ["escape"]),
                        _cache[5] || (_cache[5] = _withKeys(_withModifiers(() => { }, ["stop"]), ["enter"]))
                    ]
                }, {
                    default: _withCtx(() => [
                        _createVNode(_unref(RecCardTitle), null, {
                            default: _withCtx(() => [
                                _createTextVNode(_toDisplayString(_ctx.$t("Add Segment")), 1)
                            ]),
                            _: 1
                        }),
                        _createVNode(_unref(RecCardContent), null, {
                            default: _withCtx(() => [
                                _createVNode(_unref(RecTextField), {
                                    modelValue: newSegmentForm.value.name,
                                    "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event) => ((newSegmentForm.value.name) = $event)),
                                    label: _ctx.$t('Segment Name'),
                                    errors: (_unref(validations).name.$errors?.map((err) => err.$message) ?? []),
                                    large: "",
                                    required: "",
                                    "is-focused": "",
                                    onKeydown: _withKeys(onSaveSegmentForm, ["enter"])
                                }, null, 8, ["modelValue", "label", "errors"])
                            ]),
                            _: 1
                        }),
                        _createVNode(_unref(RecCardFooter), null, {
                            default: _withCtx(() => [
                                _createElementVNode("button", {
                                    class: "btn btn-primary",
                                    disabled: !newSegmentForm.value.name.length,
                                    type: "button",
                                    onClick: _withModifiers(onSaveSegmentForm, ["stop"])
                                }, _toDisplayString(_ctx.$t("Save")), 9, _hoisted_3),
                                _createElementVNode("button", {
                                    class: "btn btn-secondary",
                                    type: "button",
                                    onClick: _withModifiers(onCloseAddSegmentDialog, ["stop"])
                                }, _toDisplayString(_ctx.$t("Cancel")), 1)
                            ]),
                            _: 1
                        })
                    ]),
                    _: 1
                }, 8, ["modelValue"])
            ], 64));
        };
    }
});
